import { PLAN_INTERACTION_METHODS, createOwnerPlanInteraction } from './plan_edit_interaction.mjs';
import { createPlanEditCommands, copyPlanEditData, copyPlanEditTargets } from './plan_edit_commands.mjs';
const sync = value => {
  if (value && typeof value.then === 'function') {
    Promise.resolve(value).catch(() => undefined);
    throw new TypeError('Plan command ports must be synchronous');
  }
  return value;
};
const result = status => Object.freeze({ status });

/** Registration is inert; the viewport supplies its own context, authority and history. */
export function createPlanEditRegistry() {
  const records = new Map();
  function register(owner, adapter) {
    if (typeof owner !== 'string' || !/^[a-z0-9]+(?:[._-][a-z0-9]+)*$/.test(owner) || owner.length > 128
      || records.has(owner) || records.size >= 128 || !adapter || typeof adapter !== 'object') return false;
    const methods = {};
    for (const key of ['supports', 'execute', 'select']) {
      const property = Object.getOwnPropertyDescriptor(adapter, key);
      if (typeof property?.value !== 'function') return false;
      methods[key] = property.value;
    }
    let interaction = null;
    const contribution = Object.getOwnPropertyDescriptor(adapter, 'interaction');
    if (contribution) {
      if (!Object.hasOwn(contribution, 'value') || !contribution.value || typeof contribution.value !== 'object') return false;
      interaction = {};
      for (const name of PLAN_INTERACTION_METHODS) {
        const method = Object.getOwnPropertyDescriptor(contribution.value, name);
        if (typeof method?.value !== 'function') return false;
        interaction[name] = method.value;
      }
      Object.freeze(interaction);
    }
    records.set(owner, { adapter, interaction, methods: Object.freeze(methods), sessions: new Set() });
    return true;
  }
  function unregister(owner, adapter) {
    const record = records.get(owner);
    if (!record || record.adapter !== adapter) return false;
    records.delete(owner);
    for (const session of [...record.sessions]) session.dispose();
    return true;
  }
  function createSession(owner, { getContext, getSelection, canExecute, history, clipboard, synchronize }) {
    const record = records.get(owner);
    if (!record) throw new TypeError('Unregistered plan command owner');
    if (![clipboard?.read, clipboard?.write, synchronize].every(fn => typeof fn === 'function')) throw new TypeError('Missing viewport command ports');
    let disposed = false, busy = false, failed = false;
    const current = () => !disposed && records.get(owner) === record;
    const methods = record.methods;
    const commands = createPlanEditCommands({ owner, getContext, getSelection, history,
      canExecute: action => current() && !failed && sync(canExecute(action)) === true,
      adapter: {
        supports: (...args) => current() && sync(methods.supports(...args)) === true,
        execute(...args) {
          if (!current()) return result('blocked');
          const output = copyPlanEditData(sync(methods.execute(...args)));
          if (output?.selection !== undefined) copyPlanEditTargets(output.selection, owner);
          if (output?.clipboard !== undefined && output.clipboard?.owner !== owner) throw new TypeError('Foreign clipboard result');
          return output;
        }
      }
    });
    async function run(action, source = {}) {
      if (!current() || busy || failed) return result('blocked');
      busy = true; let committed = false;
      try {
        let payload = source;
        if (action === 'paste') {
          const input = copyPlanEditData(source);
          if (!input || typeof input !== 'object' || Array.isArray(input)) throw new TypeError('Invalid paste payload');
          payload = { ...input, clipboard: sync(clipboard.read()) };
        }
        const prepared = commands.prepare(action, payload);
        if (prepared.status !== 'ready') return prepared;
        const outcome = commands.execute(prepared.token);
        if (outcome.status === 'failed') { failed = true; return Object.freeze({ ...outcome, committed: null }); }
        if (outcome.status !== 'accepted') return outcome;
        if (!current()) return Object.freeze({ status: 'stale', committed: action !== 'copy' });
        if (action === 'copy') {
          if (!outcome.clipboard) throw new TypeError('Copy returned no data');
          sync(clipboard.write(outcome.clipboard));
          return outcome;
        }
        committed = true;
        if (outcome.selection !== undefined && sync(methods.select(outcome.selection)) !== true) throw new TypeError('Selection update failed');
        // From this point the model is committed. A failed presentation never means
        // the edit was rolled back: keep the session blocked until an explicit rebuild.
        await synchronize({ action });
        if (!current()) return Object.freeze({ status: 'stale', committed: true });
        return Object.freeze({ ...outcome, committed: true, synchronized: true });
      } catch {
        failed = true;
        return Object.freeze({ status: 'failed', code: 'PLAN_EDIT_SESSION_FAILED', committed });
      } finally { busy = false; }
    }
    const session = Object.freeze({ run,
      dispose() { if (disposed) return; disposed = true; commands.dispose(); record.sessions.delete(session); },
      snapshot: () => Object.freeze({ owner, busy, failed, disposed })
    });
    record.sessions.add(session);
    return session;
  }
  function createInteractionSession(owner, ports) {
    const record = records.get(owner);
    if (!record?.interaction) throw new TypeError('No native viewport interaction provider');
    const session = createOwnerPlanInteraction(owner, record.interaction, {...ports,
      canInteract: () => records.get(owner) === record && ports.canInteract(),
      onDispose: () => record.sessions.delete(session)});
    record.sessions.add(session); return session;
  }
  return Object.freeze({ register, unregister, createSession, createInteractionSession,
    snapshot: () => Object.freeze({ owners: records.size, sessions: [...records.values()].reduce((sum, entry) => sum + entry.sessions.size, 0) }),
    dispose() { for (const [owner, record] of [...records]) unregister(owner, record.adapter); }
  });
}
